import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Loader,
  SegmentedControl,
  Stack,
  Text,
} from "@mantine/core";
import { useGetEmailTemplatePreview } from "../api/campaignPreviews";
import { useGetEmailLayout } from "../api/campaigns";
import { EMAIL_TEMPLATES } from "../components/Templates/emailTemplates";
import { AuthContext } from "../services/context";
import { getCurrentWorkspace } from "../services/userConfig";
import { usePageTitle } from "../services/HeaderTitleContext";

const WIDTHS = { desktop: 600, mobile: 375 };

const TemplatePreviewDetail = () => {
  const { templateName } = useParams();
  const [view, setView] = useState("desktop");
  const iframeRef = useRef(null);

  const { user } = useContext(AuthContext);
  const { eventId } = getCurrentWorkspace(user);

  const template = EMAIL_TEMPLATES.find((t) => t.name === templateName);
  const isEventScoped = template?.hasEventBanner ?? false;

  const { data: layoutHtml, isPending: isLayoutPending } = useGetEmailLayout(
    isEventScoped && templateName === "layout" ? eventId : null
  );

  const { data: templateHtml, isPending: isTemplatePending } =
    useGetEmailTemplatePreview({
      templateName: templateName !== "layout" ? templateName : null,
      eventId: isEventScoped ? eventId : null,
    });

  usePageTitle(template?.label ?? templateName);

  const html = useMemo(() => {
    if (templateName === "layout") {
      if (!layoutHtml) return null;
      return layoutHtml.replace("{{body}}", "<p>(Campaign content here)</p>");
    }
    return templateHtml || null;
  }, [templateName, layoutHtml, templateHtml]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe && html) {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(html);
        doc.close();
      }
    }
  }, [html]);

  const isPending =
    templateName === "layout" ? isLayoutPending : isTemplatePending;

  // "layout" is a valid built-in; otherwise the name must match a known template.
  const isKnownTemplate = templateName === "layout" || !!template;

  if (!isKnownTemplate) {
    return <Text c="red">Template not found</Text>;
  }

  return (
    <Container size="xl">
      {isPending && <Loader />}
      {!isPending && html && (
        <Stack gap="sm">
          <SegmentedControl
            value={view}
            onChange={setView}
            data={[
              { value: "desktop", label: "Desktop" },
              { value: "mobile", label: "Mobile" },
            ]}
            fullWidth
          />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
              borderRadius: 8,
              padding: 16,
              minHeight: 400,
            }}
          >
            <iframe
              ref={iframeRef}
              sandbox="allow-same-origin"
              title="Email Preview"
              style={{
                width: WIDTHS[view],
                height: 500,
                border: "1px solid #e0e0e0",
                borderRadius: 4,
                backgroundColor: "#ffffff",
                transition: "width 0.2s ease",
              }}
            />
          </div>
        </Stack>
      )}
    </Container>
  );
};

export default TemplatePreviewDetail;
